
import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import firmwareVersionData from "@/data/firmware-version.json";

// Version produced by the latest PlatformIO build (scripts/auto_version.py)
const BUILD_VERSION = (firmwareVersionData?.version ?? "").replace(/^v/, "");

const detectFirmwareVersion = async (file: File): Promise<string | null> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const text = new TextDecoder("latin1").decode(bytes);
  const matches = Array.from(text.matchAll(/v(\d+)\.(\d+)\.(\d+)/gi));
  const appMajor = Number.parseInt(BUILD_VERSION.split(".")[0] ?? "", 10);

  const candidates = matches
    .filter((match) => {
      const index = match.index ?? 0;
      const context = text.slice(Math.max(0, index - 32), index + match[0].length + 16);
      return !/esp-idf|arduino|dirty/i.test(context);
    })
    .map((match) => ({
      version: `${match[1]}.${match[2]}.${match[3]}`,
      major: Number.parseInt(match[1], 10),
    }));

  const matchingAppMajor = Number.isNaN(appMajor)
    ? candidates
    : candidates.filter((candidate) => candidate.major === appMajor);
  return (matchingAppMajor[0] ?? candidates[0])?.version ?? null;
};
import { 
  ArrowUp, 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  AlertTriangle,
  RefreshCw
} from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, parseISO } from "date-fns";

interface Device {
  device_id: string;
  name?: string;
  location?: string;
  last_seen?: string;
  online?: boolean;
  hardware_type?: string;
  series?: string | null;
}

interface Firmware {
  id: number;
  filename: string;
  version: string;
  target_type: string;
  description: string;
  file_path: string;
  created_at: string;
  series: string;
  released: boolean;
  released_at: string | null;
}

interface FirmwareUpdate {
  id: number;
  device_id: string;
  firmware_id: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  created_at: string;
  updated_at: string;
  firmware?: Firmware;
  device?: Device;
}

const FirmwareManager = () => {
  const { toast } = useToast();
  const [devices, setDevices] = useState<Device[]>([]);
  const [masterFirmwares, setMasterFirmwares] = useState<Firmware[]>([]);
  const [slaveFirmwares, setSlaveFirmwares] = useState<Firmware[]>([]);
  const [updates, setUpdates] = useState<FirmwareUpdate[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [versionSource, setVersionSource] = useState<"file" | "fallback" | "manual">("fallback");
  const [uploadDetails, setUploadDetails] = useState({
    version: BUILD_VERSION,
    targetType: "master",
    series: "ALL",
    description: ""
  });
  
  const [isDeployDialogOpen, setIsDeployDialogOpen] = useState(false);
  const [selectedFirmware, setSelectedFirmware] = useState<Firmware | null>(null);
  const [deployTarget, setDeployTarget] = useState<"all" | "single">("single");
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [firmwareToDelete, setFirmwareToDelete] = useState<Firmware | null>(null);

  useEffect(() => {
    fetchDevices();
    fetchFirmwares();
    fetchUpdates();
    
    const updateSubscription = supabase
      .channel('firmware-updates')
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'firmware_updates' }, 
        (payload) => {
          console.log("Firmware update changed:", payload);
          fetchUpdates();
          
          if (payload.new.status === 'completed') {
            toast({
              title: "Update Completed",
              description: `Device ${payload.new.device_id} was updated successfully`,
            });
          } else if (payload.new.status === 'failed') {
            toast({
              variant: "destructive",
              title: "Update Failed",
              description: `Device ${payload.new.device_id} update failed`,
            });
          }
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(updateSubscription);
    };
  }, [toast]);

  const fetchDevices = async () => {
    try {
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .order('last_seen', { ascending: false });
        
      if (error) throw error;
      
      if (data) {
        console.log('All devices fetched from database:', data);
        
        const devicesWithOnlineStatus = data.map(device => {
          const now = new Date();
          const lastSeen = device.last_seen ? new Date(device.last_seen) : null;
          const timeDifference = lastSeen ? now.getTime() - lastSeen.getTime() : Infinity;
          const isOnline = timeDifference < 300000;
          
          console.log(`Device ${device.device_id} - Last seen: ${device.last_seen}, Time diff: ${timeDifference}ms, Online: ${isOnline}`);
          
          return {
            ...device,
            online: isOnline
          };
        });
        
        setDevices(devicesWithOnlineStatus);
        if (!selectedDeviceId && devicesWithOnlineStatus.length > 0) {
          setSelectedDeviceId(devicesWithOnlineStatus[0].device_id);
        }
      }
    } catch (error: any) {
      console.error('Error fetching devices:', error);
      toast({
        variant: "destructive",
        title: "Error loading devices",
        description: error.message
      });
    }
  };

  const fetchFirmwares = async () => {
    try {
      const { data, error } = await supabase
        .from('firmwares')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      if (data) {
        const firmwares = data as Firmware[];
        setMasterFirmwares(firmwares.filter(f => f.target_type === 'master'));
        setSlaveFirmwares(firmwares.filter(f => f.target_type === 'slave'));
      }
    } catch (error: any) {
      console.error('Error fetching firmwares:', error);
      toast({
        variant: "destructive",
        title: "Error loading firmware",
        description: error.message
      });
    }
  };

  const fetchUpdates = async () => {
    try {
      const { data, error } = await supabase
        .from('firmware_updates')
        .select(`
          *,
          firmware:firmware_id(id, filename, version, target_type),
          device:device_id(device_id, name, location)
        `)
        .order('created_at', { ascending: false })
        .limit(10);
        
      if (error) throw error;
      
      if (data) {
        setUpdates(data as FirmwareUpdate[]);
      }
    } catch (error: any) {
      console.error('Error fetching updates:', error);
      toast({
        variant: "destructive",
        title: "Error loading update history",
        description: error.message
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    const detectedVersion = await detectFirmwareVersion(file);
    setVersionSource(detectedVersion ? "file" : "fallback");
    setUploadDetails((details) => ({
      ...details,
      version: detectedVersion ?? BUILD_VERSION,
    }));
  };

  const uploadFirmware = async () => {
    if (!selectedFile) {
      toast({
        variant: "destructive",
        title: "No file selected",
        description: "Please select a firmware file to upload"
      });
      return;
    }
    
    if (!uploadDetails.version) {
      toast({
        variant: "destructive",
        title: "Version required",
        description: "Please provide a version number"
      });
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Series-aware filename so every series keeps its own binary
      const series = (uploadDetails.series || 'ALL').trim().toUpperCase();
      const safe = (s: string) => s.replace(/[^a-zA-Z0-9._-]/g, '-');
      const fileName = `${safe(uploadDetails.targetType)}-${safe(series)}-${safe(uploadDetails.version)}.bin`;
      
      const options = {
        cacheControl: '3600',
        upsert: true
      };

      const { data: fileData, error: fileError } = await supabase.storage
        .from('firmware')
        .upload(fileName, selectedFile, options);
        
      if (fileError) throw fileError;
      
      const { data: publicUrlData } = await supabase.storage
        .from('firmware')
        .getPublicUrl(fileName);
      
      const { error: dbError } = await supabase
        .from('firmwares')
        .insert({
          filename: selectedFile.name,
          version: uploadDetails.version,
          target_type: uploadDetails.targetType,
          series,
          released: false,
          description: uploadDetails.description,
          file_path: publicUrlData.publicUrl
        });
        
      if (dbError) throw dbError;
      
      toast({
        title: "Firmware uploaded (draft)",
        description: `${uploadDetails.targetType} v${uploadDetails.version} for series ${series} — not yet released`
      });
      
      setSelectedFile(null);
      setVersionSource("fallback");
      setUploadDetails({
        version: BUILD_VERSION,
        targetType: "master",
        series,
        description: ""
      });
      
      fetchFirmwares();
      
    } catch (error: any) {
      console.error('Error uploading firmware:', error);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error.message
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Older devices (firmware below v2.x) ignore the series-aware file name and
  // always download "<master|slave>-firmware.bin". Keep that fixed name pointing
  // at the newest released binary so those devices can still update.
  const syncLegacyAlias = async (targetType: string, sourceFileName: string) => {
    const legacyName = `${targetType}-firmware.bin`;
    if (!sourceFileName || sourceFileName === legacyName) return;
    await supabase.storage.from('firmware').remove([legacyName]);
    const { error } = await supabase.storage
      .from('firmware')
      .copy(sourceFileName, legacyName);
    if (error) throw error;
  };

  const toggleRelease = async (firmware: Firmware) => {
    try {
      const next = !firmware.released;
      const { error } = await supabase
        .from('firmwares')
        .update({ released: next, released_at: next ? new Date().toISOString() : null })
        .eq('id', firmware.id);
      if (error) throw error;

      let legacyNote = '';
      if (next) {
        try {
          const sourceFileName = firmware.file_path.split('/').pop() || '';
          await syncLegacyAlias(firmware.target_type, sourceFileName);
        } catch (aliasError: any) {
          legacyNote = ` — older devices could not be updated: ${aliasError.message}`;
        }
      }

      toast({
        title: next ? `Released to ${firmware.series}` : "Rollout withdrawn",
        description: `${firmware.target_type} v${firmware.version} (${firmware.series})${legacyNote}`
      });
      fetchFirmwares();
    } catch (error: any) {
      toast({ variant: "destructive", title: "Action failed", description: error.message });
    }
  };

  const updateDeviceSeries = async (deviceId: string, series: string) => {
    try {
      const value = series.trim().toUpperCase() || null;
      const { error } = await supabase
        .from('devices')
        .update({ series: value })
        .eq('device_id', deviceId);
      if (error) throw error;
      setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, series: value } : d));
      toast({ title: "Series saved", description: `${deviceId} → ${value ?? '—'}` });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Save failed", description: error.message });
    }
  };

  const openDeployDialog = (firmware: Firmware) => {
    setSelectedFirmware(firmware);
    setIsDeployDialogOpen(true);
  };

  const getDevicesForFirmware = (firmwareType: string) => {
    console.log(`=== DEVICE FILTERING DEBUG ===`);
    console.log(`Filtering for firmware type: ${firmwareType}`);
    console.log(`Total devices available: ${devices.length}`);
    
    devices.forEach((device, index) => {
      console.log(`Device ${index + 1}:`, {
        device_id: device.device_id,
        name: device.name || 'NO NAME',
        hardware_type: device.hardware_type || 'NO HARDWARE TYPE',
        location: device.location || 'NO LOCATION',
        online: device.online
      });
    });

    let filteredDevices;
    
    if (firmwareType === 'slave') {
      filteredDevices = devices.filter(device => {
        const deviceName = (device.name || '').toLowerCase();
        const hardwareType = (device.hardware_type || '').toLowerCase();
        const location = (device.location || '').toLowerCase();
        
        const isExplicitlyMaster = deviceName.includes('master') || location.includes('master');
        
        console.log(`Checking device ${device.device_id}: isExplicitlyMaster = ${isExplicitlyMaster}`);
        
        return !isExplicitlyMaster;
      });
    } else {
      filteredDevices = devices.filter(device => {
        const deviceName = (device.name || '').toLowerCase();
        const hardwareType = (device.hardware_type || '').toLowerCase();
        const location = (device.location || '').toLowerCase();
        
        const isMaster = deviceName.includes('master') || 
                        location.includes('master') ||
                        hardwareType.includes('esp32');
        
        console.log(`Checking device ${device.device_id}: isMaster = ${isMaster}`);
        
        return isMaster;
      });
    }
    
    console.log(`Filtered devices for ${firmwareType}:`, filteredDevices.map(d => ({
      id: d.device_id,
      name: d.name,
      hardware_type: d.hardware_type,
      location: d.location
    })));
    console.log(`=== END DEVICE FILTERING DEBUG ===`);
    
    return filteredDevices;
  };

  const deployFirmware = async () => {
    if (!selectedFirmware) return;
    
    setIsDeploying(true);
    
    try {
      if (deployTarget === "all") {
        const devicesToUpdate = getDevicesForFirmware(selectedFirmware.target_type);
        
        console.log(`Found ${devicesToUpdate.length} devices to update for ${selectedFirmware.target_type}:`, 
                   devicesToUpdate.map(d => ({ id: d.device_id, name: d.name, type: d.hardware_type })));
        
        if (devicesToUpdate.length === 0) {
          toast({
            variant: "destructive",
            title: "No matching devices",
            description: `No devices found for target type: ${selectedFirmware.target_type}`
          });
          setIsDeploying(false);
          return;
        }
        
        const updateEntries = devicesToUpdate.map(device => ({
          device_id: device.device_id,
          firmware_id: selectedFirmware.id,
          status: 'pending' as const
        }));
        
        const { error } = await supabase
          .from('firmware_updates')
          .insert(updateEntries);
          
        if (error) throw error;
        
        toast({
          title: "Updates Scheduled",
          description: `Scheduled updates for ${updateEntries.length} ${selectedFirmware.target_type} devices`
        });
      } else {
        if (!selectedDeviceId) {
          toast({
            variant: "destructive",
            title: "No device selected",
            description: "Please select a device to update"
          });
          setIsDeploying(false);
          return;
        }
        
        const { error } = await supabase
          .from('firmware_updates')
          .insert({
            device_id: selectedDeviceId,
            firmware_id: selectedFirmware.id,
            status: 'pending'
          });
          
        if (error) throw error;
        
        toast({
          title: "Update Scheduled",
          description: `Scheduled update for device ${selectedDeviceId}`
        });
      }
      
      setIsDeployDialogOpen(false);
      fetchUpdates();
      
    } catch (error: any) {
      console.error('Error deploying firmware:', error);
      toast({
        variant: "destructive",
        title: "Deployment Failed",
        description: error.message
      });
    } finally {
      setIsDeploying(false);
    }
  };

  const openDeleteDialog = (firmware: Firmware) => {
    setFirmwareToDelete(firmware);
    setIsDeleteDialogOpen(true);
  };

  const deleteFirmware = async () => {
    if (!firmwareToDelete) return;
    
    try {
      // Remove any device update records referencing this firmware first
      const { error: usageError } = await supabase
        .from('firmware_updates')
        .delete()
        .eq('firmware_id', firmwareToDelete.id);

      if (usageError) throw usageError;

      
      const filename = firmwareToDelete.file_path.split('/').pop();
      if (filename) {
        const { error: storageError } = await supabase.storage
          .from('firmware')
          .remove([filename]);
          
        if (storageError) throw storageError;
      }
      
      const { error } = await supabase
        .from('firmwares')
        .delete()
        .eq('id', firmwareToDelete.id);
        
      if (error) throw error;
      
      toast({
        title: "Firmware Deleted",
        description: "The firmware has been successfully deleted"
      });
      
      setIsDeleteDialogOpen(false);
      fetchFirmwares();
      
    } catch (error: any) {
      console.error('Error deleting firmware:', error);
      toast({
        variant: "destructive",
        title: "Deletion Failed",
        description: error.message
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-100 text-red-800">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const knownSeries = Array.from(new Set(
    [...masterFirmwares, ...slaveFirmwares].map(f => f.series)
      .concat(devices.map(d => d.series || ''))
      .concat(['ALL'])
      .filter(Boolean)
  )).sort();

  const renderFirmwareTable = (firmwares: Firmware[], title: string) => {
    const grouped = firmwares.reduce<Record<string, Firmware[]>>((acc, f) => {
      const key = f.series || 'ALL';
      (acc[key] ||= []).push(f);
      return acc;
    }, {});
    const seriesKeys = Object.keys(grouped).sort();

    return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>Grouped by series — devices only fetch released versions</CardDescription>
      </CardHeader>
      <CardContent>
        {firmwares.length > 0 ? (
          <div className="space-y-6">
            {seriesKeys.map((seriesKey) => (
              <div key={seriesKey}>
                <h4 className="mb-2 text-sm font-semibold">Series {seriesKey}</h4>
                <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Filename</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped[seriesKey].map((firmware) => (
                <TableRow key={firmware.id}>
                  <TableCell className="font-medium">{firmware.version}</TableCell>
                  <TableCell>
                    {firmware.released ? (
                      <Badge variant="outline" className="bg-green-100 text-green-800">Released</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Draft</Badge>
                    )}
                  </TableCell>
                  <TableCell>{firmware.filename}</TableCell>
                  <TableCell className="max-w-xs truncate">{firmware.description}</TableCell>
                  <TableCell className="text-sm">
                    {format(parseISO(firmware.created_at), 'MMM d, HH:mm')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant={firmware.released ? "outline" : "default"}
                        size="sm"
                        onClick={() => toggleRelease(firmware)}
                      >
                        {firmware.released ? 'Withdraw' : `Release to ${firmware.series}`}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openDeployDialog(firmware)}
                      >
                        <ArrowUp className="h-4 w-4 mr-1" />
                        Deploy
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => openDeleteDialog(firmware)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
                </Table>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium mb-2">No {title} Firmware Available</h3>
            <p>Upload your first {title.toLowerCase()} firmware file to get started</p>
          </div>
        )}
      </CardContent>
    </Card>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Navigation />
      
      <div className="flex-1 container mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold mb-6">Firmware Manager</h1>
        
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Upload New Firmware */}
          <Card>
            <CardHeader>
              <CardTitle>Upload New Firmware</CardTitle>
              <CardDescription>Upload new firmware files for your ESP32 devices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="firmware-file">Firmware Binary (.bin)</Label>
                  <Input 
                    id="firmware-file" 
                    type="file" 
                    accept=".bin"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  {selectedFile && (
                    <p className="text-sm text-gray-500">{selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)</p>
                  )}
                </div>
                
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="version">Version</Label>
                  <Input 
                    id="version" 
                    placeholder="e.g. 1.0.0"
                    value={uploadDetails.version}
                    onChange={(e) => {
                      setVersionSource("manual");
                      setUploadDetails({...uploadDetails, version: e.target.value});
                    }}
                    disabled={isUploading}
                  />
                  <p className="text-sm text-muted-foreground">
                    {versionSource === "file"
                      ? `Detected from selected firmware (v${uploadDetails.version})`
                      : versionSource === "manual"
                        ? "Version entered manually"
                      : BUILD_VERSION
                        ? `Using website fallback (v${BUILD_VERSION}); selecting firmware detects its version`
                        : "Select firmware to detect its version automatically"}
                  </p>
                </div>
                
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="target-type">Target Device Type</Label>
                  <Select 
                    value={uploadDetails.targetType} 
                    onValueChange={(value) => setUploadDetails({...uploadDetails, targetType: value})}
                    disabled={isUploading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select target device type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="master">Master ESP32</SelectItem>
                      <SelectItem value="slave">Slave ESP32</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="series">Series</Label>
                  <Input
                    id="series"
                    placeholder="e.g. S1, S2 or ALL"
                    list="series-suggestions"
                    value={uploadDetails.series}
                    onChange={(e) => setUploadDetails({...uploadDetails, series: e.target.value})}
                    disabled={isUploading}
                  />
                  <datalist id="series-suggestions">
                    {knownSeries.map((s) => <option key={s} value={s} />)}
                  </datalist>
                  <p className="text-xs text-gray-500">ALL = valid for all series (fallback)</p>
                </div>
                

                
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="description">Description</Label>
                  <Input 
                    id="description" 
                    placeholder="What's new in this version?"
                    value={uploadDetails.description}
                    onChange={(e) => setUploadDetails({...uploadDetails, description: e.target.value})}
                    disabled={isUploading}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              {isUploading ? (
                <div className="flex items-center space-x-2 w-full">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span>{uploadProgress}%</span>
                </div>
              ) : (
                <Button 
                  className="w-full" 
                  onClick={uploadFirmware} 
                  disabled={!selectedFile || !uploadDetails.version}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Firmware
                </Button>
              )}
            </CardFooter>
          </Card>
          
          {/* Recent Updates */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Updates</CardTitle>
              <CardDescription>Recent firmware update attempts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[260px] overflow-y-auto">
                {updates.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Device</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {updates.map((update) => (
                        <TableRow key={update.id}>
                          <TableCell>{update.device?.name || update.device_id}</TableCell>
                          <TableCell>{update.firmware?.version}</TableCell>
                          <TableCell>{getStatusBadge(update.status)}</TableCell>
                          <TableCell className="text-sm">
                            {format(parseISO(update.created_at), 'MMM d, HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>No update history found</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => fetchUpdates()}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </CardFooter>
          </Card>
        </div>
        
        {/* Master Firmware Table */}
        {renderFirmwareTable(masterFirmwares, "Master Firmware")}
        
        {/* Slave Firmware Table */}
        {renderFirmwareTable(slaveFirmwares, "Slave Firmware")}

        {/* Series assignment per device */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Series per device</CardTitle>
            <CardDescription>Assign each device to a series (S1, S2, ...). A device with its own series setting on its local page ignores this value.</CardDescription>
          </CardHeader>
          <CardContent>
            {devices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device</TableHead>
                    <TableHead>MAC</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[220px]">Series</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.device_id}>
                      <TableCell>{device.name || device.location || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{device.device_id}</TableCell>
                      <TableCell>
                        {device.online
                          ? <Badge variant="outline" className="bg-green-100 text-green-800">Online</Badge>
                          : <Badge variant="outline">Offline</Badge>}
                      </TableCell>
                      <TableCell>
                        <Input
                          defaultValue={device.series || ''}
                          placeholder="e.g. S1"
                          list="series-suggestions"
                          onBlur={(e) => {
                            const v = e.target.value;
                            if ((v.trim().toUpperCase() || null) !== (device.series || null)) {
                              updateDeviceSeries(device.device_id, v);
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-gray-500">No devices known yet.</p>
            )}
          </CardContent>
        </Card>

        
        {/* Debug Info Card - Moved to bottom */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800">Debug Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm space-y-2">
              <p><strong>Total devices in database:</strong> {devices.length}</p>
              <p><strong>Online devices:</strong> {devices.filter(d => d.online).length}</p>
              <p><strong>Master firmware versions:</strong> {masterFirmwares.length}</p>
              <p><strong>Slave firmware versions:</strong> {slaveFirmwares.length}</p>
              <p><strong>Devices:</strong></p>
              <ul className="ml-4 space-y-1">
                {devices.map((device, index) => (
                  <li key={device.device_id} className="text-xs">
                    {index + 1}. <strong>{device.device_id}</strong> 
                    {device.name && ` (Name: ${device.name})`}
                    {device.hardware_type && ` (Type: ${device.hardware_type})`}
                    {device.location && ` (Location: ${device.location})`}
                    {device.online ? ' [ONLINE]' : ' [OFFLINE]'}
                    {device.last_seen && ` - Last seen: ${new Date(device.last_seen).toLocaleString()}`}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
        
        {/* Deploy Dialog */}
        <Dialog open={isDeployDialogOpen} onOpenChange={setIsDeployDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deploy Firmware</DialogTitle>
              <DialogDescription>
                Deploy firmware version {selectedFirmware?.version} ({selectedFirmware?.target_type}) to your ESP32 devices
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="flex flex-col space-y-2">
                <Label>Deployment Target</Label>
                <div className="flex gap-4">
                  <div className="flex items-center">
                    <input 
                      type="radio" 
                      id="single-device" 
                      name="deploy-target" 
                      className="mr-2"
                      checked={deployTarget === "single"} 
                      onChange={() => setDeployTarget("single")}
                    />
                    <Label htmlFor="single-device">Single Device</Label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="radio" 
                      id="all-devices" 
                      name="deploy-target"
                      className="mr-2"
                      checked={deployTarget === "all"} 
                      onChange={() => setDeployTarget("all")} 
                    />
                    <Label htmlFor="all-devices">All Compatible Devices</Label>
                  </div>
                </div>
              </div>
              
              {deployTarget === "single" && selectedFirmware && (
                <div className="space-y-2">
                  <Label htmlFor="device-select">Select Device</Label>
                  {(() => {
                    const compatibleDevices = getDevicesForFirmware(selectedFirmware.target_type);
                    return (
                      <>
                        <Select 
                          value={selectedDeviceId || undefined} 
                          onValueChange={setSelectedDeviceId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a device" />
                          </SelectTrigger>
                          <SelectContent>
                            {compatibleDevices.map((device) => (
                              <SelectItem key={device.device_id} value={device.device_id}>
                                {device.name || device.location || device.device_id} 
                                {device.hardware_type && ` (${device.hardware_type})`}
                                {device.online ? ' [ONLINE]' : ' [OFFLINE]'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="text-xs text-gray-600 mt-2 p-3 bg-gray-50 rounded">
                          <p><strong>Found {compatibleDevices.length} compatible {selectedFirmware.target_type} device(s)</strong></p>
                          {compatibleDevices.length > 0 && (
                            <div className="mt-2">
                              <p>Compatible devices:</p>
                              <ul className="ml-4 mt-1">
                                {compatibleDevices.map(device => (
                                  <li key={device.device_id} className="text-xs">
                                    • {device.device_id} 
                                    {device.name && ` (${device.name})`}
                                    {device.hardware_type && ` - ${device.hardware_type}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {compatibleDevices.length === 0 && (
                            <div className="text-red-600 mt-1">
                              <p>No {selectedFirmware.target_type} devices found.</p>
                              <p>Check the debug info above for all available devices.</p>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
              
              {deployTarget === "all" && selectedFirmware && (
                <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Deploy to all {selectedFirmware.target_type} devices</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        This will deploy firmware version {selectedFirmware.version} to all compatible {selectedFirmware.target_type} devices ({getDevicesForFirmware(selectedFirmware.target_type).length} found).
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeployDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={deployFirmware} 
                disabled={isDeploying || (deployTarget === "single" && !selectedDeviceId)}
              >
                {isDeploying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Deploy Firmware
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Firmware</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete firmware version {firmwareToDelete?.version}?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteFirmware}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default FirmwareManager;
